import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, CornerLeftUp, Folder, FolderOpen, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { get, post, put } from '../api';
import { Drawer, ErrorBox, Loading, Modal, useToast } from '../components/ui';

/** Extensiones que entiende el motor de OCR. */
const EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'bmp', 'gif'];

export interface ManagedFolder {
  id: string;
  name: string;
  enabled: boolean;
  input: string;
  output: string;
  recursive: boolean;
  extensions: string[];
  mode: 'skip-text' | 'force';
  afterSuccess: 'move' | 'delete' | 'keep';
  archiveDir: string;
  errorDir: string;
  outputSuffix: string;
  stableSeconds: number;
  usePolling: boolean;
  pollIntervalSeconds: number;
}

interface AgentConfig {
  agent: { id: number; name: string; hostname: string | null; online: boolean; streaming: boolean };
  managed: {
    roots: string[];
    remoteBrowse: boolean;
    revision: number;
    appliedRevision: number;
    pending: boolean;
    error: string | null;
    folderCount: number;
  };
  folders: ManagedFolder[];
}

interface BrowseResult {
  path: string | null;
  parent: string | null;
  roots: string[];
  entries: { name: string; path: string }[];
  writable: boolean;
  truncated: boolean;
}

/** El agente puede ser Windows o Linux: el separador se deduce de sus propias rutas. */
const sepOf = (path: string) => (/^[A-Za-z]:|\\/.test(path) ? '\\' : '/');
const joinPath = (dir: string, name: string) => {
  const sep = sepOf(dir);
  return dir.endsWith(sep) ? `${dir}${name}` : `${dir}${sep}${name}`;
};

const newFolder = (root: string): ManagedFolder => ({
  id: crypto.randomUUID(),
  name: 'Escáner',
  enabled: true,
  input: root ? joinPath(root, 'entrada') : '',
  output: root ? joinPath(root, 'salida') : '',
  recursive: false,
  extensions: ['pdf', 'jpg', 'jpeg', 'png', 'tif', 'tiff'],
  mode: 'skip-text',
  afterSuccess: 'move',
  archiveDir: '',
  errorDir: '',
  outputSuffix: '',
  stableSeconds: 5,
  usePolling: false,
  pollIntervalSeconds: 10,
});

// ------------------------------------------------------------ explorador remoto

function FolderPicker({
  agentId,
  startPath,
  onPick,
  onClose,
}: {
  agentId: number;
  startPath: string;
  onPick: (path: string) => void;
  onClose: () => void;
}) {
  const [path, setPath] = useState<string | null>(startPath || null);
  const [newName, setNewName] = useState('');

  const browse = useQuery({
    queryKey: ['agent-browse', agentId, path],
    queryFn: () => post<BrowseResult>(`/agents/${agentId}/browse`, { path }),
    retry: false,
    // El equipo remoto puede tardar: no se relanza sola mientras el administrador mira.
    staleTime: 30_000,
  });

  const current = browse.data?.path ?? null;

  return (
    <Modal
      title="Carpetas del equipo"
      onClose={onClose}
      width={620}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            disabled={!current}
            onClick={() => {
              if (!current) return;
              onPick(newName.trim() ? joinPath(current, newName.trim()) : current);
              onClose();
            }}
          >
            {newName.trim() ? 'Usar la subcarpeta' : 'Usar esta carpeta'}
          </button>
        </>
      }
    >
      <ErrorBox error={browse.error} />
      <div className="field">
        <label>Carpeta actual</label>
        <div className="mono" style={{ wordBreak: 'break-all' }}>
          {current ?? 'Carpetas permitidas de este equipo'}
        </div>
        {browse.data && current && !browse.data.writable && (
          <span className="hint" style={{ color: 'var(--danger)' }}>
            El agente no puede escribir aquí: sirve como entrada, no como destino.
          </span>
        )}
      </div>

      {browse.isFetching ? (
        <Loading label="Preguntando al equipo…" />
      ) : (
        <div className="card" style={{ maxHeight: 280, overflowY: 'auto' }}>
          {browse.data?.parent && (
            <button className="btn btn-sm" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => setPath(browse.data!.parent)}>
              <CornerLeftUp size={14} /> Subir
            </button>
          )}
          {current && (
            <button className="btn btn-sm" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => setPath(null)}>
              <Folder size={14} /> Carpetas permitidas
            </button>
          )}
          {browse.data?.entries.length === 0 ? (
            <p className="muted" style={{ margin: 8 }}>
              No hay subcarpetas.
            </p>
          ) : (
            browse.data?.entries.map((e) => (
              <button
                key={e.path}
                className="btn btn-sm"
                style={{ width: '100%', justifyContent: 'flex-start' }}
                onClick={() => setPath(e.path)}
                title={e.path}
              >
                <FolderOpen size={14} /> {e.name} <ChevronRight size={13} className="faint" />
              </button>
            ))
          )}
          {browse.data?.truncated && <p className="cell-sub" style={{ margin: 8 }}>Hay más subcarpetas de las que se muestran.</p>}
        </div>
      )}

      {current && (
        <div className="field" style={{ marginTop: 12 }}>
          <label htmlFor="pick-new">Subcarpeta nueva (opcional)</label>
          <input
            id="pick-new"
            className="input"
            placeholder="El agente la crea si no existe"
            value={newName}
            onChange={(e) => setNewName(e.target.value.replace(/[\\/:*?"<>|]/g, ''))}
          />
        </div>
      )}
    </Modal>
  );
}

// ------------------------------------------------------------ editor de una carpeta

function PathInput({
  id,
  label,
  hint,
  value,
  agentId,
  roots,
  canBrowse,
  onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  agentId: number;
  roots: string[];
  canBrowse: boolean;
  onChange: (value: string) => void;
}) {
  const [picking, setPicking] = useState(false);
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <input id={id} className="input mono" value={value} placeholder={hint} onChange={(e) => onChange(e.target.value)} />
        <button className="btn" type="button" disabled={!canBrowse} onClick={() => setPicking(true)} title={canBrowse ? 'Explorar el equipo' : 'El agente no está conectado'}>
          <Folder size={14} /> Examinar
        </button>
      </div>
      {hint && <span className="hint">{hint}</span>}
      {picking && (
        <FolderPicker
          agentId={agentId}
          startPath={value || roots[0] || ''}
          onPick={onChange}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}

function FolderCard({
  folder,
  index,
  agentId,
  roots,
  canBrowse,
  onChange,
  onRemove,
}: {
  folder: ManagedFolder;
  index: number;
  agentId: number;
  roots: string[];
  canBrowse: boolean;
  onChange: (next: ManagedFolder) => void;
  onRemove: () => void;
}) {
  const [advanced, setAdvanced] = useState(false);
  const set = <K extends keyof ManagedFolder>(key: K, value: ManagedFolder[K]) => onChange({ ...folder, [key]: value });
  const idp = `mf${index}`;

  return (
    <div className="card card-pad" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <input className="input" style={{ fontWeight: 600 }} value={folder.name} maxLength={120} onChange={(e) => set('name', e.target.value)} aria-label="Nombre de la carpeta" />
        <label className="checkbox" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={folder.enabled} onChange={(e) => set('enabled', e.target.checked)} />
          <span>Activa</span>
        </label>
        <button className="btn btn-icon danger" type="button" onClick={onRemove} aria-label="Quitar carpeta" title="Quitar carpeta">
          <Trash2 size={16} />
        </button>
      </div>

      <PathInput
        id={`${idp}-in`}
        label="Carpeta de entrada (donde deja los archivos el escáner)"
        value={folder.input}
        agentId={agentId}
        roots={roots}
        canBrowse={canBrowse}
        onChange={(v) => set('input', v)}
      />
      <PathInput
        id={`${idp}-out`}
        label="Carpeta de destino (PDF con texto)"
        value={folder.output}
        agentId={agentId}
        roots={roots}
        canBrowse={canBrowse}
        onChange={(v) => set('output', v)}
      />

      <div className="field-row">
        <div className="field">
          <label htmlFor={`${idp}-after`}>Al terminar, el original…</label>
          <select id={`${idp}-after`} className="select" value={folder.afterSuccess} onChange={(e) => set('afterSuccess', e.target.value as ManagedFolder['afterSuccess'])}>
            <option value="move">Se mueve a «procesados»</option>
            <option value="delete">Se borra</option>
            <option value="keep">Se queda donde está</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor={`${idp}-suffix`}>Sufijo del PDF</label>
          <input id={`${idp}-suffix`} className="input" placeholder="p. ej. _ocr" value={folder.outputSuffix} onChange={(e) => set('outputSuffix', e.target.value)} />
          <span className="hint">factura.pdf → factura{folder.outputSuffix}.pdf</span>
        </div>
      </div>

      <button className="btn btn-sm" type="button" onClick={() => setAdvanced(!advanced)}>
        {advanced ? 'Ocultar' : 'Más'} opciones
      </button>

      {advanced && (
        <div style={{ marginTop: 12 }}>
          <div className="field-row">
            <div className="field">
              <label htmlFor={`${idp}-mode`}>OCR</label>
              <select id={`${idp}-mode`} className="select" value={folder.mode} onChange={(e) => set('mode', e.target.value as ManagedFolder['mode'])}>
                <option value="skip-text">Solo páginas sin texto</option>
                <option value="force">Todas las páginas</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor={`${idp}-stable`}>Espera antes de enviar (s)</label>
              <input
                id={`${idp}-stable`}
                className="input"
                type="number"
                min={1}
                max={600}
                value={folder.stableSeconds}
                onChange={(e) => set('stableSeconds', Number(e.target.value))}
              />
              <span className="hint">Tiempo sin cambios para dar el archivo por terminado</span>
            </div>
          </div>

          {folder.afterSuccess === 'move' && (
            <PathInput
              id={`${idp}-arch`}
              label="Carpeta de procesados (opcional)"
              hint="Por defecto: <entrada>/procesados"
              value={folder.archiveDir}
              agentId={agentId}
              roots={roots}
              canBrowse={canBrowse}
              onChange={(v) => set('archiveDir', v)}
            />
          )}
          <PathInput
            id={`${idp}-err`}
            label="Carpeta de errores (opcional)"
            hint="Por defecto: <entrada>/errores"
            value={folder.errorDir}
            agentId={agentId}
            roots={roots}
            canBrowse={canBrowse}
            onChange={(v) => set('errorDir', v)}
          />

          <div className="field">
            <label>Tipos de archivo</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {EXTENSIONS.map((ext) => (
                <label key={ext} className="checkbox" style={{ marginBottom: 0 }}>
                  <input
                    type="checkbox"
                    checked={folder.extensions.includes(ext)}
                    onChange={(e) =>
                      set('extensions', e.target.checked ? [...new Set([...folder.extensions, ext])] : folder.extensions.filter((x) => x !== ext))
                    }
                  />
                  <span>{ext.toUpperCase()}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="checkbox">
            <input type="checkbox" checked={folder.recursive} onChange={(e) => set('recursive', e.target.checked)} />
            <span>Incluir subcarpetas</span>
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={folder.usePolling} onChange={(e) => set('usePolling', e.target.checked)} />
            <span>Carpeta de red: comprobar cada cierto tiempo en vez de esperar avisos del sistema</span>
          </label>
          {folder.usePolling && (
            <div className="field">
              <label htmlFor={`${idp}-poll`}>Intervalo de comprobación (s)</label>
              <input
                id={`${idp}-poll`}
                className="input"
                type="number"
                min={2}
                max={3600}
                value={folder.pollIntervalSeconds}
                onChange={(e) => set('pollIntervalSeconds', Number(e.target.value))}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------ panel del agente

export function AgentFoldersDrawer({ agentId, agentName, onClose }: { agentId: number; agentName: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<ManagedFolder[] | null>(null);

  const config = useQuery({
    queryKey: ['agent-config', agentId],
    queryFn: () => get<AgentConfig>(`/agents/${agentId}/config`),
    // Mientras el agente confirma la revisión aplicada.
    refetchInterval: 10_000,
  });

  const save = useMutation({
    mutationFn: (folders: ManagedFolder[]) => put<{ revision: number; notified: boolean }>(`/agents/${agentId}/config`, { folders }),
    onSuccess: async (res) => {
      setDraft(null);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['agent-config', agentId] }),
        qc.invalidateQueries({ queryKey: ['agents'] }),
      ]);
      toast(res.notified ? 'Configuración enviada al equipo' : 'Guardada. El equipo la recogerá cuando vuelva a conectarse');
    },
  });

  const managed = config.data?.managed;
  const folders = draft ?? config.data?.folders ?? [];
  const dirty = draft !== null;
  const canBrowse = !!(config.data?.agent.streaming && managed?.remoteBrowse);
  const roots = managed?.roots ?? [];

  const update = (index: number, next: ManagedFolder) => setDraft(folders.map((f, i) => (i === index ? next : f)));

  return (
    <Drawer
      title={`Carpetas de ${agentName}`}
      onClose={onClose}
      headerExtra={
        <p className="cell-sub">
          {config.data?.agent.hostname}
          {config.data?.agent.online ? ' · conectado' : ' · desconectado'}
        </p>
      }
    >
      <ErrorBox error={config.error} />
      {config.isLoading ? (
        <Loading />
      ) : !roots.length ? (
        <div className="alert alert-info">
          <AlertTriangle size={16} />
          <div>
            <strong>La configuración remota está desactivada en este equipo.</strong>
            <p style={{ margin: '6px 0 0' }}>
              Por seguridad, el panel solo puede usar carpetas que se autoricen en el propio equipo. Ejecuta allí, como
              administrador:
            </p>
            <code style={{ display: 'block', margin: '8px 0' }}>ocr1sk-agent allow-folders C:\Escaneos</code>
            <p className="cell-sub" style={{ margin: 0 }}>
              (en Linux, por ejemplo <code>/srv/escaneos</code>) y reinicia el servicio del agente. Mientras tanto, las
              carpetas se configuran en el panel local del equipo.
            </p>
          </div>
        </div>
      ) : (
        <>
          {managed?.error && (
            <div className="alert alert-error">
              <AlertTriangle size={16} />
              <div>
                <strong>El equipo no pudo aplicar el último cambio.</strong>
                <p style={{ margin: '4px 0 0' }}>{managed.error}</p>
              </div>
            </div>
          )}
          {managed?.pending && !managed.error && (
            <div className="alert alert-warn">
              <RefreshCw size={16} />
              <span>
                Cambios enviados, pendientes de que el equipo los aplique
                {config.data?.agent.online ? ' (menos de un minuto)' : '; está desconectado, los recogerá al volver'}.
              </span>
            </div>
          )}
          {!canBrowse && (
            <p className="cell-sub">
              El explorador de carpetas necesita que el equipo esté conectado; mientras no lo esté, escribe las rutas a mano.
            </p>
          )}
          <p className="cell-sub" style={{ marginBottom: 14 }}>
            Carpetas permitidas en el equipo: {roots.map((r) => <code key={r} style={{ marginRight: 6 }}>{r}</code>)}
          </p>

          <ErrorBox error={save.error} />

          {folders.map((folder, index) => (
            <FolderCard
              key={folder.id}
              folder={folder}
              index={index}
              agentId={agentId}
              roots={roots}
              canBrowse={canBrowse}
              onChange={(next) => update(index, next)}
              onRemove={() => setDraft(folders.filter((_, i) => i !== index))}
            />
          ))}
          {!folders.length && <p className="muted">Todavía no has definido carpetas para este equipo desde el panel.</p>}

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="btn" type="button" onClick={() => setDraft([...folders, newFolder(roots[0] ?? '')])}>
              <Plus size={16} /> Añadir carpeta
            </button>
            <div style={{ flex: 1 }} />
            {dirty && (
              <button className="btn" type="button" onClick={() => setDraft(null)} disabled={save.isPending}>
                Descartar
              </button>
            )}
            <button className="btn btn-primary" type="button" disabled={!dirty || save.isPending} onClick={() => save.mutate(folders)}>
              {save.isPending && <Loader2 size={14} className="spin" />} Guardar y enviar
            </button>
          </div>
        </>
      )}
    </Drawer>
  );
}
